import React, { useState, useEffect } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardTitle,
  CardText,
  Row,
  Col,
  InputGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { Triangle } from "react-loader-spinner";

import toast from "react-hot-toast";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import DaybookService from "../../services/OrderingServices/DaybookService";
import CustomDataTable from "../../Components/CustomTable/CustomTable";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import SettingService from "../../services/OrderingServices/SettingService";
import CloseSummaryPrint from "../../OrderingSystem/Dashboard/components/CardComponent/CloseSummaryPrint";
import ConfirmationDaybook from "../../OrderingSystem/Dashboard/components/CardComponent/ConfirmationDaybook";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const DaybookOrderSummary = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [isDayOpen, setIsDayOpen] = useState(null);
  const [isDayClosed, setIsDayClosed] = useState(false);
  const [orderData, setOrderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responseModal, setResponseModal] = useState(false);
  const [responseData, setResponseData] = useState([]);
  const toggleResponse = () => {
    setResponseModal(!responseModal);
  };

  const toggleConfirm = () => {
    setShowCloseModal(!showCloseModal);
  };

  // Initialize date range
  useEffect(() => {
    const today = new Date();
    const oneMonthBefore = new Date();
    oneMonthBefore.setMonth(today.getMonth() - 1);
    setStartDate(oneMonthBefore);
    setEndDate(today);
  }, []);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [paymentResult, orderResult, settingResult] =
          await Promise.allSettled([
            DaybookService.paymentTypeSummary(),
            DaybookService.orderSummary(),
            SettingService.getDaySetting(),
          ]);

        if (paymentResult.status === "rejected") {
          console.error(
            "Error fetching payment summary:",
            paymentResult.reason
          );
          toast.error(
            paymentResult.reason?.response?.data?.message ||
              "Payment summary failed"
          );
        }

        if (orderResult.status === "fulfilled") {
          setSummary(orderResult.value || []);
          setOrderData(orderResult.value || []);
        } else {
          console.error("Error fetching order summary:", orderResult.reason);
        }

        if (settingResult.status === "fulfilled") {
          setIsDayOpen(settingResult.value.isActive);
          setIsDayClosed(!settingResult.value.isActive);
        } else {
          console.error("Error fetching day setting:", settingResult.reason);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle day toggle

  const handleDayToggle = async () => {
    const newStatus = !isDayOpen;
    console.log("🚀 ~ handleDayToggle ~ newStatus:", newStatus);

    // If trying to close the day (newStatus = false) and there are orders, show modal
    if (!newStatus) {
      setShowCloseModal(true);
      return;
    }

    // Proceed with day toggle
    await performDayToggle(newStatus);
  };

  // Perform day toggle
  const performDayToggle = async (newStatus) => {
    setIsDayOpen(newStatus);
    setLoading(true);
    try {
      let response;
      if (!newStatus) {
        // closing day
        const response = await DaybookService.closeSummary();
        setResponseData(response);
        setIsDayOpen(false); // explicitly set
        toggleResponse();
        toast.success("Day is closed!!");
      } else {
        // opening day
        const response = await SettingService.setOpenDay();
        setIsDayOpen(true); // explicitly set
        toast.success(response);
      }
    } catch (error) {
      console.error("Error updating day setting:", error);
      toast.error(error?.response?.data);
      setIsDayOpen(!newStatus); // Rollback state
    } finally {
      setLoading(false);
    }
  };

  // Handle print
  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 500);
  };

  // Status badge rendering
  const getStatusBadge = (status, defaultText = "No Data") => {
    const statusStyles = {
      Ordered: "bg-warning",
      Preparing: "bg-secondary",
      Ready: "bg-primary",
      Completed: "bg-success",
      Cancelled: "bg-danger",
      Paid: "bg-success",
      Pending: "bg-warning",
      Failed: "bg-danger",
      Processing: "bg-secondary",
      Refunded: "bg-primary",
      PartiallyRefunded: "bg-secondary",
      OrderCancelled: "bg-danger",
      Chargeback: "bg-secondary",
    };
    return (
      <span className={`badge ${statusStyles[status] || "bg-danger"}`}>
        {status || defaultText}
      </span>
    );
  };

  // DataTable columns
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "75px",
    },
    {
      name: "Order Ref Id",
      selector: (row) => (
        <Link title={row.orderRefrenceId} to={`/order/${row.id}`}>
          {row.orderRefrenceId}
        </Link>
      ),
      sortable: true,
      width: "14vw",
    },
    { name: "Table Name", selector: (row) => row.tableName, sortable: true },
    {
      name: "Order Status",
      selector: (row) => row.orderStatus,
      cell: (row) => getStatusBadge(row.orderStatus),
      sortable: true,
    },
    {
      name: "Payment",
      selector: (row) => row.paymentStatus,
      cell: (row) => getStatusBadge(row.paymentStatus),
      sortable: true,
    },
    {
      name: "Discount",
      selector: (row) => row.discountAmount,
      sortable: true,
      right: true,
    },
    {
      name: "Total Amt",
      selector: (row) => row.grandTotal,
      sortable: true,
      right: true,
    },
  ];

  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb
          title="Day Close"
          pageTitle="Order Summary"
          breadcrumbItems={breadcrumbItems}
        />
        <Card className="shadow-sm border-0 rounded-3">
          <CardBody>
            <Row className="gy-3 align-items-center">
              {/* Start Date */}
              <Col sm={3}>
                <Label className="fw-semibold text-muted mb-1">
                  Start Date
                </Label>
                <Flatpickr
                  value={startDate}
                  onChange={([date]) => setStartDate(date)}
                  options={{
                    dateFormat: "d/m/Y",
                    maxDate: new Date(),
                  }}
                  className="form-control shadow-sm"
                />
              </Col>
              {/* End Date */}
              <Col sm={3}>
                <Label className="fw-semibold text-muted mb-1">End Date</Label>
                <Flatpickr
                  value={endDate}
                  onChange={([date]) => setEndDate(date)}
                  options={{
                    dateFormat: "d/m/Y",
                    minDate: startDate,
                    maxDate: new Date(),
                  }}
                  className="form-control shadow-sm"
                />
              </Col>
              {/* Actions */}
              <Col sm={6}>
                <div className="d-flex justify-content-end gap-4 align-items-center">
                  {/* Toggle Switch */}
                  <div className="d-flex align-items-center justify-content-center align-content-center gap-2">
                    <div
                      className="form-check form-switch form-switch-lg"
                      dir="ltr"
                    >
                      <input
                        type="checkbox"
                        className="form-check-input day-switch"
                        id="customSwitchsizelg"
                        checked={isDayOpen}
                        onChange={handleDayToggle}
                        disabled={isLoading}
                        style={{
                          backgroundColor: isDayOpen ? "#28a745" : "#dc3545",
                          borderColor: isDayOpen ? "#28a745" : "#dc3545",
                        }}
                      />
                    </div>
                    <Label
                      htmlFor="customSwitchsizelg"
                      className={`mb-0 fw-bold ${
                        isDayClosed ? "text-danger" : "text-success"
                      }`}
                    >
                      {isDayClosed ? "Day Closed" : "Day Open"}
                    </Label>
                  </div>
                  {/* Print Button */}
                  <Button
                    color="primary"
                    className="d-flex align-items-center gap-2"
                    onClick={handlePrint}
                    size="sm"
                    disabled={isPrinting}
                  >
                    <i className="ri-printer-line fs-5"></i>
                  </Button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
        {/* Table */}
        <Row>
          <Col xs={12}>
            <Card className="shadow-sm">
              <CardBody>
                {isLoading ? (
                  <div className="d-flex justify-content-center my-4">
                    <Triangle
                      visible={true}
                      height="60"
                      width="60"
                      color="#5B71B9"
                      ariaLabel="triangle-loading"
                    />
                  </div>
                ) : summary.length > 0 ? (
                  <CustomDataTable
                    responsive
                    striped
                    pagination
                    fixedHeader
                    persistTableHead
                    progressPending={isLoading}
                    customStyles={customStyles}
                    progressComponent={
                      <div className="my-3">
                        <Triangle
                          visible={true}
                          height="80"
                          width="80"
                          color="#5B71B9"
                          ariaLabel="triangle-loading"
                        />
                        <h5 className="mt-1">Loading...</h5>
                      </div>
                    }
                    columns={columns}
                    data={summary}
                  />
                ) : (
                  <div className="text-center py-3">
                    <p className="text-muted mb-0">No payment records found.</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        {/* Confirmation Modal */}
        {/* <Modal
          isOpen={showCloseModal}
          toggle={() => setShowCloseModal(false)}
          backdrop="static"
          keyboard={false}
        >
          <ModalHeader
            toggle={() => setShowCloseModal(false)}
            className="border-bottom-0"
          >
            <span className="text-warning">
              <i className="ri-alert-line me-2"></i>
              Confirm Day Close
            </span>
          </ModalHeader>
          <ModalBody>
            <div className="text-center">
              <div className="mb-3">
                <i
                  className="ri-error-warning-line text-warning"
                  style={{ fontSize: "3rem" }}
                ></i>
              </div>
              <h6 className="mb-3">
                You have {orderData.length} order(s) for today.
              </h6>
              <p className="text-muted mb-0">
                Are you sure you want to close the day? This action will
                finalize all transactions for today.
              </p>
            </div>
          </ModalBody>
          <ModalFooter className="border-top-0 justify-content-center">
            <Button
              color="secondary"
              onClick={() => setShowCloseModal(false)}
              className="me-2"
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={() => {
                setShowCloseModal(false);
                performDayToggle(false);
              }}
            >
              <i className="ri-check-line me-1"></i>
              Yes, Close Day
            </Button>
          </ModalFooter>
        </Modal> */}

        <ConfirmationDaybook
          isOpen={showCloseModal}
          toggle={toggleConfirm}
          orderData={orderData}
          performDayToggle={performDayToggle}
        />

        <CloseSummaryPrint
          isOpen={responseModal}
          toggle={toggleResponse}
          data={responseData}
        />
      </div>
    </div>
  );
};

export default DaybookOrderSummary;
